import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import MDBox from "components/MDBox";

const BLOCK_FORMAT_OPTIONS = [
  { label: "Paragraph", value: "p" },
  { label: "Heading 1", value: "h1" },
  { label: "Heading 2", value: "h2" },
  { label: "Heading 3", value: "h3" },
  { label: "Quote", value: "blockquote" },
];

const buildEmptyTableHtml = (rows, cols) => {
  const safeRows = Math.min(Math.max(rows, 1), 20);
  const safeCols = Math.min(Math.max(cols, 1), 10);
  let html =
    '<table style="border-collapse:collapse;width:100%;margin:8px 0;"><tbody>';
  for (let row = 0; row < safeRows; row += 1) {
    html += "<tr>";
    for (let col = 0; col < safeCols; col += 1) {
      html +=
        '<td style="border:1px solid #ccc;padding:8px;vertical-align:top;">&nbsp;</td>';
    }
    html += "</tr>";
  }
  html += "</tbody></table><p><br></p>";
  return html;
};

const toolbarButtonSx = {
  border: "1px solid #ddd",
  borderRadius: "4px",
  width: 32,
  height: 32,
};

function ToolbarIconButton({ title, icon, onClick, color = "default" }) {
  return (
    <Tooltip title={title}>
      <IconButton size="small" color={color} sx={toolbarButtonSx} onClick={onClick}>
        <Icon fontSize="small">{icon}</Icon>
      </IconButton>
    </Tooltip>
  );
}

ToolbarIconButton.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
};

function ColorPickerButton({ title, icon, defaultColor, command, onApply }) {
  const inputRef = useRef(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (event) => {
    onApply(command, event.target.value);
    event.target.value = defaultColor;
  };

  return (
    <Tooltip title={title}>
      <IconButton size="small" sx={toolbarButtonSx} onClick={handleClick}>
        <Icon fontSize="small">{icon}</Icon>
        <input
          ref={inputRef}
          type="color"
          defaultValue={defaultColor}
          onChange={handleChange}
          style={{
            position: "absolute",
            opacity: 0,
            width: 0,
            height: 0,
            pointerEvents: "none",
          }}
          tabIndex={-1}
          aria-hidden
        />
      </IconButton>
    </Tooltip>
  );
}

ColorPickerButton.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  defaultColor: PropTypes.string.isRequired,
  command: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
};

function HtmlNoteEditor({
  value,
  onChange,
  placeholder = "Enter formatted content...",
  minHeight = 200,
}) {
  const editorRef = useRef(null);
  const [showSource, setShowSource] = useState(false);
  const [blockFormat, setBlockFormat] = useState("p");
  const isInternalUpdateRef = useRef(false);

  const syncFromEditor = useCallback(() => {
    if (!editorRef.current) {
      return;
    }
    isInternalUpdateRef.current = true;
    onChange(editorRef.current.innerHTML);
  }, [onChange]);

  useEffect(() => {
    if (showSource || !editorRef.current) {
      return;
    }
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }
    const nextHtml = value || "";
    if (editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, [value, showSource]);

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const runCommand = (command, arg = null) => {
    focusEditor();
    document.execCommand(command, false, arg);
    syncFromEditor();
  };

  const applyBlockFormat = (tag) => {
    setBlockFormat(tag);
    runCommand("formatBlock", tag);
  };

  const handleInsertTable = () => {
    const rows = parseInt(window.prompt("Number of rows?", "3"), 10) || 3;
    const cols = parseInt(window.prompt("Number of columns?", "3"), 10) || 3;
    focusEditor();
    document.execCommand("insertHTML", false, buildEmptyTableHtml(rows, cols));
    syncFromEditor();
  };

  const handleInsertLink = () => {
    const url = window.prompt("Link URL", "https://");
    if (!url) {
      return;
    }
    runCommand("createLink", url);
  };

  return (
    <MDBox
      sx={{
        border: "1px solid #ccc",
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <MDBox
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={0.5}
        px={1}
        py={0.75}
        sx={{ borderBottom: "1px solid #ddd", bgcolor: "#f8f9fa" }}
      >
        <FormControl size="small" sx={{ minWidth: 132 }}>
          <Select
            value={blockFormat}
            onChange={(event) => applyBlockFormat(event.target.value)}
            displayEmpty
            sx={{
              height: 32,
              fontSize: "0.8125rem",
              bgcolor: "#fff",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd" },
            }}
          >
            {BLOCK_FORMAT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.8125rem" }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        <ToolbarIconButton title="Bold" icon="format_bold" onClick={() => runCommand("bold")} />
        <ToolbarIconButton title="Italic" icon="format_italic" onClick={() => runCommand("italic")} />
        <ToolbarIconButton
          title="Underline"
          icon="format_underlined"
          onClick={() => runCommand("underline")}
        />
        <ToolbarIconButton
          title="Strikethrough"
          icon="format_strikethrough"
          onClick={() => runCommand("strikeThrough")}
        />

        <ColorPickerButton
          title="Text color"
          icon="format_color_text"
          defaultColor="#222222"
          command="foreColor"
          onApply={runCommand}
        />
        <ColorPickerButton
          title="Highlight color"
          icon="format_color_fill"
          defaultColor="#fff59d"
          command="hiliteColor"
          onApply={runCommand}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        <ToolbarIconButton
          title="Bullet list"
          icon="format_list_bulleted"
          onClick={() => runCommand("insertUnorderedList")}
        />
        <ToolbarIconButton
          title="Numbered list"
          icon="format_list_numbered"
          onClick={() => runCommand("insertOrderedList")}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        <ToolbarIconButton
          title="Align left"
          icon="format_align_left"
          onClick={() => runCommand("justifyLeft")}
        />
        <ToolbarIconButton
          title="Align center"
          icon="format_align_center"
          onClick={() => runCommand("justifyCenter")}
        />
        <ToolbarIconButton
          title="Align right"
          icon="format_align_right"
          onClick={() => runCommand("justifyRight")}
        />
        <ToolbarIconButton
          title="Justify"
          icon="format_align_justify"
          onClick={() => runCommand("justifyFull")}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        <ToolbarIconButton title="Insert table" icon="table_chart" onClick={handleInsertTable} />
        <ToolbarIconButton title="Insert link" icon="link" onClick={handleInsertLink} />
        <ToolbarIconButton
          title="Horizontal line"
          icon="horizontal_rule"
          onClick={() => runCommand("insertHorizontalRule")}
        />
        <ToolbarIconButton
          title="Clear formatting"
          icon="format_clear"
          onClick={() => runCommand("removeFormat")}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        <ToolbarIconButton
          title={showSource ? "Visual editor" : "HTML source"}
          icon="code"
          color={showSource ? "info" : "default"}
          onClick={() => setShowSource((current) => !current)}
        />
      </MDBox>

      {showSource ? (
        <TextField
          multiline
          minRows={8}
          fullWidth
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              fontFamily: "Consolas, Monaco, monospace",
              fontSize: "0.85rem",
            },
            "& fieldset": { border: "none" },
          }}
        />
      ) : (
        <MDBox
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={syncFromEditor}
          onBlur={syncFromEditor}
          data-placeholder={placeholder}
          sx={{
            minHeight,
            px: 2,
            py: 1.5,
            outline: "none",
            overflowX: "auto",
            typography: "body1",
            "&:empty:before": {
              content: "attr(data-placeholder)",
              color: "text.disabled",
            },
            "& h1": { fontSize: "1.75rem", fontWeight: 700, my: 1 },
            "& h2": { fontSize: "1.4rem", fontWeight: 700, my: 1 },
            "& h3": { fontSize: "1.15rem", fontWeight: 600, my: 1 },
            "& p": { my: 0.75 },
            "& blockquote": {
              borderLeft: "3px solid #ccc",
              pl: 2,
              ml: 0,
              my: 1,
              color: "text.secondary",
            },
            "& table": {
              borderCollapse: "collapse",
              width: "100%",
              my: 1,
            },
            "& th, & td": {
              border: "1px solid #ccc",
              p: 1,
              verticalAlign: "top",
            },
            "& img": { maxWidth: "100%" },
            "& hr": { border: "none", borderTop: "1px solid #ccc", my: 2 },
          }}
        />
      )}
    </MDBox>
  );
}

HtmlNoteEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minHeight: PropTypes.number,
};

export default HtmlNoteEditor;
